import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { 
  ChevronLeft, 
  ChevronRight, 
  Maximize2,
  X,
  ArrowRight,
  Database,
  BarChart3,
  FileText
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Link } from "react-router-dom";

const walkthroughData = {
  "data-refinery": {
    name: "DataRefinery",
    icon: Database,
    color: "#00B4D8",
    steps: [
      {
        title: "Secure Login",
        caption: "Enterprise-grade authentication with role-based access. Sign in to your dedicated DataRefinery workspace.",
        image: "https://customer-assets.emergentagent.com/job_eaa678b7-9af1-4aad-8a6a-f3c851cbd4dd/artifacts/kdojswh0_DR1.jpeg"
      },
      {
        title: "Dashboard Overview",
        caption: "See your entire procurement landscape at a glance — total spend, supplier count, data quality scores, spend by category, and classification accuracy metrics.",
        image: "https://customer-assets.emergentagent.com/job_eb677b80-1d6f-4298-b1f5-516bcc846e81/artifacts/apiq24lg_DR2.jpeg"
      },
      {
        title: "Upload & Process Data",
        caption: "Upload CSV, Excel, or JSON files. Choose to create a new session, append to existing, or replace data. AI processes your data through cleansing, normalization, and L1-L4 classification.",
        image: "https://customer-assets.emergentagent.com/job_eb677b80-1d6f-4298-b1f5-516bcc846e81/artifacts/t75eyl28_DR3.jpeg"
      },
      {
        title: "Processing Results",
        caption: "Review AI processing outcomes — data quality scores, classification confidence, issues detected, and multi-item splitting results. All in one view.",
        image: "https://customer-assets.emergentagent.com/job_eb677b80-1d6f-4298-b1f5-516bcc846e81/artifacts/7fwb1kks_DR4.jpeg"
      },
      {
        title: "Processed & Classified Data",
        caption: "Browse your cleansed, classified data with L1-L4 categories, confidence scores, and full search/filter capabilities. Export to CSV anytime.",
        image: "https://customer-assets.emergentagent.com/job_eb677b80-1d6f-4298-b1f5-516bcc846e81/artifacts/lboiy6lf_DR5.jpeg"
      },
      {
        title: "API Connectors",
        caption: "Configure API connectors for automated data ingestion from ERPs, P-Cards, and other systems. Push spend data directly into DataRefinery via secure endpoints.",
        image: "https://customer-assets.emergentagent.com/job_eb677b80-1d6f-4298-b1f5-516bcc846e81/artifacts/0rczumre_DR9.jpeg"
      },
      {
        title: "Category Taxonomy",
        caption: "Create custom L1-L4 classification taxonomies for bespoke categorization. The AI maps spend records exclusively to your defined categories.",
        image: "https://customer-assets.emergentagent.com/job_eb677b80-1d6f-4298-b1f5-516bcc846e81/artifacts/pxf9b4cz_DR10.jpeg"
      },
      {
        title: "ML Performance",
        caption: "Monitor AI model performance and accuracy — track records processed, cleansing confidence, classification confidence, and processing pipeline progress in real time.",
        image: "https://customer-assets.emergentagent.com/job_eb677b80-1d6f-4298-b1f5-516bcc846e81/artifacts/5v60i55h_DR12.jpeg"
      },
      {
        title: "ML Insights & Corrections",
        caption: "Track how user corrections improve AI classification accuracy over time. View accuracy by category, correction patterns, and AI confidence vs actual accuracy comparisons.",
        image: "https://customer-assets.emergentagent.com/job_eb677b80-1d6f-4298-b1f5-516bcc846e81/artifacts/s6205wyc_DR13.jpeg"
      },
      {
        title: "User Management",
        caption: "Manage team members and access roles — Admins, Analysts, and Viewers. Invite users, assign organizations, and control permissions across your procurement team.",
        image: "https://customer-assets.emergentagent.com/job_eb677b80-1d6f-4298-b1f5-516bcc846e81/artifacts/3l222ci5_DR14.jpeg"
      },
      {
        title: "Activity Audit Log",
        caption: "Monitor all user actions across your organization — logins, data exports, API key creation, and more. Filter by user or category with full timestamp tracking.",
        image: "https://customer-assets.emergentagent.com/job_eb677b80-1d6f-4298-b1f5-516bcc846e81/artifacts/noamu0dc_DR15.jpeg"
      },
      {
        title: "API Keys",
        caption: "Generate and manage API keys for external integrations like SpendLens. Create, revoke, and track usage of keys with full status visibility.",
        image: "https://customer-assets.emergentagent.com/job_eb677b80-1d6f-4298-b1f5-516bcc846e81/artifacts/ip492cbj_DR16.jpeg"
      }
    ]
  },
  "spendlens": {
    name: "SpendLens",
    icon: BarChart3,
    color: "#10B981",
    steps: [
      {
        title: "Secure Login",
        caption: "Sign in to SpendLens — Spend Analytics, Cost Savings, Risk Management, and Supplier Insights. Trusted by procurement teams worldwide.",
        image: "https://customer-assets.emergentagent.com/job_eaa678b7-9af1-4aad-8a6a-f3c851cbd4dd/artifacts/i7bnel7w_01-login.png"
      },
      {
        title: "Dashboard Overview",
        caption: "Complete spend visibility — £51.6M total spend, 41K transactions, 2,156 suppliers, 28.3% maverick spend flagged. P2P coverage, spend trends, and country breakdowns at a glance.",
        image: "https://customer-assets.emergentagent.com/job_eb677b80-1d6f-4298-b1f5-516bcc846e81/artifacts/c8uqt9c4_02-dashboard.png"
      },
      {
        title: "Spend Analysis",
        caption: "Deep dive into spending patterns — £63.9M across 30 suppliers in 5 countries. Quarterly spend charts, business unit breakdowns, category trends, and single-source risk detection.",
        image: "https://customer-assets.emergentagent.com/job_eb677b80-1d6f-4298-b1f5-516bcc846e81/artifacts/hly8yejt_03-spend-analysis.png"
      },
      {
        title: "Spend Distribution",
        caption: "Multi-dimensional supplier analysis with Kraljic Matrix — Strategic (838), Leverage (240), Bottleneck (404), and Non-Critical (674) positioning. Interactive scatter plot visualization.",
        image: "https://customer-assets.emergentagent.com/job_eb677b80-1d6f-4298-b1f5-516bcc846e81/artifacts/0cku7q32_04-spend-distribution.png"
      },
      {
        title: "Category Console",
        caption: "Hierarchical category analysis — treemap visualization, L1/L2 spend breakdowns, pie charts, and segmentation views. Travel at £23.9M is the top category across 11 total.",
        image: "https://customer-assets.emergentagent.com/job_eb677b80-1d6f-4298-b1f5-516bcc846e81/artifacts/gpqn930h_05-category-console.png"
      },
      {
        title: "Commonality Analysis",
        caption: "Identify consolidation opportunities — 20 common suppliers across 4 locations, 12 high-priority consolidation targets, and 30 common items. Supplier and item commonality views.",
        image: "https://customer-assets.emergentagent.com/job_eb677b80-1d6f-4298-b1f5-516bcc846e81/artifacts/wn1xhs8k_06-commonality-analysis.png"
      },
      {
        title: "Supplier Directory",
        caption: "2,227 suppliers with risk scoring, Kraljic positioning, KYC status, and scorecard ratings. Filter by risk level, search by name, and detect 123 potential duplicates.",
        image: "https://customer-assets.emergentagent.com/job_eb677b80-1d6f-4298-b1f5-516bcc846e81/artifacts/2j7n8kp4_07-supplier-directory.png"
      },
      {
        title: "P2P Analytics",
        caption: "Procure-to-Pay process metrics — 454 POs, 91.6% PR-to-PO conversion, 52.1% maverick spend flagged, 67.7% 2-way match rate. PO vs Non-PO spend and category volume analysis.",
        image: "https://customer-assets.emergentagent.com/job_eb677b80-1d6f-4298-b1f5-516bcc846e81/artifacts/hdssoeg7_08-p2p-analytics.png"
      },
      {
        title: "Comparative Analysis",
        caption: "Year-over-year, quarter-over-quarter, or custom period comparisons. Track spend changes by category, entity, SBU, and monthly trends with absolute and percentage variance.",
        image: "https://customer-assets.emergentagent.com/job_eb677b80-1d6f-4298-b1f5-516bcc846e81/artifacts/f6dp7ktr_09-comparative-analysis.png"
      },
      {
        title: "Industry Benchmark",
        caption: "Compare your spend distribution against industry norms with AI-powered benchmarking. Select your industry, add custom benchmarks, and export results.",
        image: "https://customer-assets.emergentagent.com/job_eb677b80-1d6f-4298-b1f5-516bcc846e81/artifacts/34cqzjrb_10-industry-benchmark.png"
      },
      {
        title: "Spend Forecasting",
        caption: "AI-powered 6-month spend prediction based on 24 months of historical data. Volatile trend detection, peak month identification, AI insights on seasonal patterns and structural breaks.",
        image: "https://customer-assets.emergentagent.com/job_eb677b80-1d6f-4298-b1f5-516bcc846e81/artifacts/3xbfscz9_11-forecasting.png"
      },
      {
        title: "Multi-Currency Analytics",
        caption: "FX exposure analysis across 3 currencies — £51.7M total spend, 0.1% foreign exposure. Currency distribution pie chart, breakdown table with exchange rates, and monthly spend by currency.",
        image: "https://customer-assets.emergentagent.com/job_eb677b80-1d6f-4298-b1f5-516bcc846e81/artifacts/ts6ln2x0_12-currency-analytics.png"
      },
      {
        title: "Reports",
        caption: "Generate rich reports with charts, insights, and AI recommendations. Download Action Plans, Category Strategies, and Weekly Overviews. Schedule automated report generation.",
        image: "https://customer-assets.emergentagent.com/job_eb677b80-1d6f-4298-b1f5-516bcc846e81/artifacts/tju4obq6_13-reports.png"
      },
      {
        title: "Upload Data",
        caption: "Import spend data or supplier due diligence files — CSV and Excel supported. 63 supported columns across 10 groups. Add to existing dataset or replace all. Download template to get started.",
        image: "https://customer-assets.emergentagent.com/job_eb677b80-1d6f-4298-b1f5-516bcc846e81/artifacts/ovmeg95e_14-upload-data.png"
      },
      {
        title: "Data Quality",
        caption: "93.4/100 completeness score across 41,170 records. 11 feature groups active, field completeness by section, blocked analytics detection, and AI recommendations for improvement.",
        image: "https://customer-assets.emergentagent.com/job_eb677b80-1d6f-4298-b1f5-516bcc846e81/artifacts/0q6nfamf_15-data-quality.png"
      },
      {
        title: "Data Governance",
        caption: "72.6 health score with policy compliance tracking. Data quality at 93.5% (AI-Ready), 11.1% duplicate supplier rate, 100% classification coverage, and PDS section breakdowns.",
        image: "https://customer-assets.emergentagent.com/job_eb677b80-1d6f-4298-b1f5-516bcc846e81/artifacts/mq1uveqr_16-governance.png"
      },
      {
        title: "Data Connectors",
        caption: "Connect your ERP, accounting, and procurement systems for automated data ingestion. Integration guide included with one-click connector creation.",
        image: "https://customer-assets.emergentagent.com/job_eb677b80-1d6f-4298-b1f5-516bcc846e81/artifacts/2rmr9plp_17aa-connectors.png"
      },
      {
        title: "User Management",
        caption: "16 users across 11 organizations — Super Admin, Category Manager, Data Uploader roles. AI access controls, page-level permissions, and status management.",
        image: "https://customer-assets.emergentagent.com/job_eb677b80-1d6f-4298-b1f5-516bcc846e81/artifacts/da8has3l_17a-user-management.png"
      },
      {
        title: "Organizations",
        caption: "Multi-tenant organization management — 11 client organizations with subscription tiers, premium features, currency settings, and analytics access controls.",
        image: "https://customer-assets.emergentagent.com/job_eb677b80-1d6f-4298-b1f5-516bcc846e81/artifacts/rdm6kbyl_17b-organizations.png"
      },
      {
        title: "Audit Log",
        caption: "GDPR-compliant activity trail — 1,312 entries. Track logins, AI anonymisation calls, and all user actions with timestamps, IP addresses, and status indicators.",
        image: "https://customer-assets.emergentagent.com/job_eb677b80-1d6f-4298-b1f5-516bcc846e81/artifacts/mif4tq2h_17c-audit-log.png"
      },
      {
        title: "Settings",
        caption: "Configure organization preferences — fiscal calendar, currency, categories, account details, and privacy settings. Fiscal year auto-calculates across all analytics and reports.",
        image: "https://customer-assets.emergentagent.com/job_eb677b80-1d6f-4298-b1f5-516bcc846e81/artifacts/9blv613c_18-settings.png"
      },
      {
        title: "Help & Documentation",
        caption: "Built-in step-by-step user manual with screenshots — 22 sections covering every feature. Filter by role (Super Admin, Data Analyst, Category Manager) and download as PDF.",
        image: "https://customer-assets.emergentagent.com/job_eb677b80-1d6f-4298-b1f5-516bcc846e81/artifacts/8f75w24n_22-help.png"
      }
    ]
  },
  "contrack": {
    name: "ConTrack",
    icon: FileText,
    color: "#8B5CF6",
    steps: [
      {
        title: "Secure Login",
        caption: "Streamlined contract management starts here — enterprise authentication with centralized repository, AI-powered drafting, and automated workflows.",
        image: "https://customer-assets.emergentagent.com/job_eaa678b7-9af1-4aad-8a6a-f3c851cbd4dd/artifacts/0fz8psbh_CT1.jpeg"
      },
      {
        title: "Dashboard Overview",
        caption: "Full contract portfolio at a glance — 121 contracts worth $3.8M, expiry alerts, pending approvals, breakdowns by status, type, category, and jurisdiction.",
        image: "https://customer-assets.emergentagent.com/job_eb677b80-1d6f-4298-b1f5-516bcc846e81/artifacts/uiewdd67_CT2.jpeg"
      },
      {
        title: "Contract Register",
        caption: "Search, filter, and manage all contracts in one place — with supplier details, type, jurisdiction, value, addenda, expiration dates, and real-time status tracking.",
        image: "https://customer-assets.emergentagent.com/job_eb677b80-1d6f-4298-b1f5-516bcc846e81/artifacts/vheirs87_CT3.jpeg"
      },
      {
        title: "Contract Detail & Editor",
        caption: "Full rich-text contract editor with metadata panel — contract type, value, dates, department, supplier info, and access controls. Download PDF, edit, add addenda, or submit for review.",
        image: "https://customer-assets.emergentagent.com/job_eb677b80-1d6f-4298-b1f5-516bcc846e81/artifacts/exu4k80q_CT3a.jpeg"
      },
      {
        title: "Governed Approval Workflow",
        caption: "Multi-stage approval pipeline with segregation of duties — Review, Supplier Review, and Approval stages. External reviewers notified via email with feedback recording.",
        image: "https://customer-assets.emergentagent.com/job_eb677b80-1d6f-4298-b1f5-516bcc846e81/artifacts/4jyuprlk_CT3b.jpeg"
      },
      {
        title: "External Reviewer Portal",
        caption: "Suppliers and external reviewers get a dedicated public page to review contract content, approve, reject, or request changes — no login required.",
        image: "https://customer-assets.emergentagent.com/job_eb677b80-1d6f-4298-b1f5-516bcc846e81/artifacts/5rny10cz_CT3d.jpeg"
      },
      {
        title: "Contract Management",
        caption: "Monitor deliverables, KPIs, performance, and relationship management. Track contract status, values, and assigned owners across your entire portfolio.",
        image: "https://customer-assets.emergentagent.com/job_eb677b80-1d6f-4298-b1f5-516bcc846e81/artifacts/36frzs90_CT4a.jpeg"
      },
      {
        title: "Templates",
        caption: "Create and manage reusable contract and addendum templates with multi-section structures. Version control, duplicate, and edit templates for consistent drafting.",
        image: "https://customer-assets.emergentagent.com/job_eb677b80-1d6f-4298-b1f5-516bcc846e81/artifacts/jysjfwaj_CT5.jpeg"
      },
      {
        title: "Clause Library",
        caption: "Pre-approved clauses for contract drafting — Force Majeure, Confidentiality, and more. Mandatory clauses flagged, searchable by category for rapid drafting.",
        image: "https://customer-assets.emergentagent.com/job_eb677b80-1d6f-4298-b1f5-516bcc846e81/artifacts/swom6die_CT5a.jpeg"
      },
      {
        title: "Approval Workflows",
        caption: "Governed contract review and approval process — 75 total workflows with status breakdown across In Review, Revision, Approval, and Completed stages.",
        image: "https://customer-assets.emergentagent.com/job_eb677b80-1d6f-4298-b1f5-516bcc846e81/artifacts/zwi9r90y_CT6.jpeg"
      },
      {
        title: "Compliance Monitoring",
        caption: "Track obligations, milestones, and compliance status with AI-powered extraction. 100% compliance score visualization, overdue detection, and email reminders.",
        image: "https://customer-assets.emergentagent.com/job_eb677b80-1d6f-4298-b1f5-516bcc846e81/artifacts/h0eq9cbl_CT7.jpeg"
      },
      {
        title: "Supplier Contract Review",
        caption: "Upload and analyze supplier contracts with AI — risk scoring (out of 10), automatic analysis, and one-click import to your contract register.",
        image: "https://customer-assets.emergentagent.com/job_eb677b80-1d6f-4298-b1f5-516bcc846e81/artifacts/8kuawh9n_CT8.jpeg"
      },
      {
        title: "Reports & Analytics",
        caption: "Generate insights with value distribution charts, approval cycle times, and expiring contracts watchlist. Export to CSV, JSON, or PDF with custom date ranges.",
        image: "https://customer-assets.emergentagent.com/job_eb677b80-1d6f-4298-b1f5-516bcc846e81/artifacts/8ufdb7ko_CT9.jpeg"
      },
      {
        title: "User Management",
        caption: "Full access control — manage users with 7 role levels (System Admin, Superuser, Legal Team, Executive, Contract Creator, and more). Department assignment and status tracking.",
        image: "https://customer-assets.emergentagent.com/job_eb677b80-1d6f-4298-b1f5-516bcc846e81/artifacts/od8qhltc_CT10.jpeg"
      },
      {
        title: "Approval Policies",
        caption: "Configure rules that auto-assign reviewers and approvers based on contract attributes — value thresholds, priority levels, and required role-based sign-offs.",
        image: "https://customer-assets.emergentagent.com/job_eb677b80-1d6f-4298-b1f5-516bcc846e81/artifacts/3fnt0qci_CT10a.jpeg"
      },
      {
        title: "Audit Log",
        caption: "End-to-end audit trail for statutory compliance — 824+ activities tracked. Filter by email or action type, with contract-specific audit trails and timestamps.",
        image: "https://customer-assets.emergentagent.com/job_eb677b80-1d6f-4298-b1f5-516bcc846e81/artifacts/xclz9alv_CT14.jpeg"
      },
      {
        title: "Settings & Profile",
        caption: "Manage your account — profile information, notification preferences, security settings, and delegate access. Full control over your ConTrack experience.",
        image: "https://customer-assets.emergentagent.com/job_eb677b80-1d6f-4298-b1f5-516bcc846e81/artifacts/dchwbvbz_CT18.jpeg"
      },
      {
        title: "Help & User Guide",
        caption: "Built-in step-by-step instructions for every role — Superuser, Admin, Legal, Contract Creator, Finance, Executive, and External Reviewer. Searchable, printable, and downloadable as HTML or PDF.",
        image: "https://customer-assets.emergentagent.com/job_eb677b80-1d6f-4298-b1f5-516bcc846e81/artifacts/nbtd7aem_CT19.jpeg"
      }
    ]
  }
};

const ProductWalkthrough = ({ productId = "data-refinery" }) => {
  const [currentStep, setCurrentStep] = useState(0);
  const [fullscreen, setFullscreen] = useState(false);
  const product = walkthroughData[productId];

  if (!product || product.steps.length === 0) {
    return (
      <div className="text-center py-12">
        <p className="text-[#94A3B8] mb-4">Walkthrough coming soon for {product?.name || productId}.</p>
        <a href="mailto:info@procureaide.com">
          <Button className="bg-[#00B4D8] text-[#020617] font-bold hover:bg-[#0096B4]" data-testid="walkthrough-book-demo-btn">
            Book a Live Demo
            <ArrowRight className="ml-2 w-4 h-4" />
          </Button>
        </a>
      </div>
    );
  }

  const step = product.steps[currentStep];
  const isFirst = currentStep === 0;
  const isLast = currentStep === product.steps.length - 1;

  const goNext = () => !isLast && setCurrentStep(currentStep + 1);
  const goPrev = () => !isFirst && setCurrentStep(currentStep - 1);

  return (
    <>
      <div className="space-y-6" data-testid={`walkthrough-${productId}`}>
        {/* Step indicators */}
        <div className="flex items-center justify-between mb-2">
          <div className="flex items-center gap-2">
            {product.steps.map((s, i) => (
              <button
                key={i}
                onClick={() => setCurrentStep(i)}
                className={`h-2 rounded-full transition-all duration-300 cursor-pointer ${
                  i === currentStep 
                    ? "w-8" 
                    : i < currentStep 
                      ? "w-2 opacity-60" 
                      : "w-2 opacity-30"
                }`}
                style={{ backgroundColor: product.color }}
                data-testid={`walkthrough-step-dot-${i}`}
                aria-label={`Go to step ${i + 1}: ${s.title}`}
              />
            ))}
          </div>
          <span className="text-xs text-[#64748B]">
            {currentStep + 1} / {product.steps.length}
          </span>
        </div>

        {/* Screenshot display */}
        <div className="relative group">
          <AnimatePresence mode="wait">
            <motion.div
              key={currentStep}
              initial={{ opacity: 0, x: 20 }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: -20 }}
              transition={{ duration: 0.3 }}
              className="relative rounded-xl overflow-hidden border border-[#00B4D8]/15"
            >
              <img
                src={step.image}
                alt={step.title}
                className="w-full h-auto max-h-[500px] object-contain bg-[#0a0f1a]"
                data-testid={`walkthrough-image-${currentStep}`}
              />
              
              {/* Fullscreen button */}
              <button
                onClick={() => setFullscreen(true)}
                className="absolute top-3 right-3 p-2 rounded-lg bg-[#020617]/70 backdrop-blur-sm text-[#94A3B8] hover:text-[#F8FAFC] hover:bg-[#020617]/90 transition-all opacity-0 group-hover:opacity-100"
                data-testid="walkthrough-fullscreen-btn"
                aria-label="View fullscreen"
              >
                <Maximize2 className="w-4 h-4" />
              </button>
            </motion.div>
          </AnimatePresence>

          {/* Nav arrows */}
          {!isFirst && (
            <button
              onClick={goPrev}
              className="absolute left-3 top-1/2 -translate-y-1/2 p-2 rounded-full bg-[#020617]/70 backdrop-blur-sm text-[#F8FAFC] hover:bg-[#020617]/90 transition-all"
              data-testid="walkthrough-prev-btn"
            >
              <ChevronLeft className="w-5 h-5" />
            </button>
          )}
          {!isLast && (
            <button
              onClick={goNext}
              className="absolute right-3 top-1/2 -translate-y-1/2 p-2 rounded-full bg-[#020617]/70 backdrop-blur-sm text-[#F8FAFC] hover:bg-[#020617]/90 transition-all"
              data-testid="walkthrough-next-btn"
            >
              <ChevronRight className="w-5 h-5" />
            </button>
          )}
        </div>

        {/* Caption */}
        <AnimatePresence mode="wait">
          <motion.div
            key={currentStep}
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -10 }}
            transition={{ duration: 0.25 }}
          >
            <h3 className="text-lg font-semibold text-[#F8FAFC] mb-1" data-testid="walkthrough-step-title">
              {step.title}
            </h3>
            <p className="text-sm text-[#94A3B8] leading-relaxed" data-testid="walkthrough-step-caption">
              {step.caption}
            </p>
          </motion.div>
        </AnimatePresence>

        {/* Navigation + CTA */}
        <div className="flex items-center justify-between pt-2">
          <div className="flex gap-2">
            <Button
              variant="outline"
              size="sm"
              onClick={goPrev}
              disabled={isFirst}
              className="border-[#00B4D8]/30 text-[#94A3B8] hover:text-[#F8FAFC] hover:bg-[#111F36] disabled:opacity-30"
              data-testid="walkthrough-prev-text-btn"
            >
              <ChevronLeft className="w-4 h-4 mr-1" /> Previous
            </Button>
            <Button
              variant="outline"
              size="sm"
              onClick={goNext}
              disabled={isLast}
              className="border-[#00B4D8]/30 text-[#94A3B8] hover:text-[#F8FAFC] hover:bg-[#111F36] disabled:opacity-30"
              data-testid="walkthrough-next-text-btn"
            >
              Next <ChevronRight className="w-4 h-4 ml-1" />
            </Button>
          </div>
          {isLast && (
            <a href="mailto:info@procureaide.com">
              <Button 
                className="font-bold hover:scale-105 transition-all"
                style={{ backgroundColor: product.color, color: "#020617" }}
                data-testid="walkthrough-book-demo-cta"
              >
                Book a Live Demo
                <ArrowRight className="ml-2 w-4 h-4" />
              </Button>
            </a>
          )}
        </div>
      </div>

      {/* Fullscreen modal */}
      <AnimatePresence>
        {fullscreen && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-[100] bg-[#020617]/95 backdrop-blur-md flex items-center justify-center p-4"
            onClick={() => setFullscreen(false)}
            data-testid="walkthrough-fullscreen-modal"
          >
            <button
              onClick={() => setFullscreen(false)}
              className="absolute top-4 right-4 p-2 rounded-full bg-[#111F36] text-[#F8FAFC] hover:bg-[#1a2d4a] transition-colors z-10"
              data-testid="walkthrough-fullscreen-close"
            >
              <X className="w-6 h-6" />
            </button>
            <motion.img
              initial={{ scale: 0.9 }}
              animate={{ scale: 1 }}
              exit={{ scale: 0.9 }}
              src={step.image}
              alt={step.title}
              className="max-w-full max-h-[90vh] object-contain rounded-lg"
              onClick={(e) => e.stopPropagation()}
            />
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
};

export { walkthroughData };
export default ProductWalkthrough;
